import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL, apiUrl } from '../config/api';

const parseStockValue = (value) => {
  const normalized = String(value ?? '').trim();

  if (!normalized) {
    return 0;
  }

  const normalizedDecimal = normalized.replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(normalizedDecimal)) {
    throw new Error(`Valoarea stocului trebuie să fie un număr pozitiv sau zero. Valoare primită: ${value}`);
  }

  return Number(normalizedDecimal);
};

const detectDelimiter = (line) => {
  const commaCount = (line.match(/,/g) || []).length;
  const semicolonCount = (line.match(/;/g) || []).length;
  return semicolonCount > commaCount ? ';' : ',';
};

const parseCSVLine = (line, delimiter) => {
  const values = [];
  let current = '';
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const nextChar = line[index + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === delimiter && !inQuotes) {
      values.push(current.trim());
      current = '';
      continue;
    }

    current += char;
  }

  values.push(current.trim());
  return values;
};

const emptyForm = {
  cod: '',
  denumire: '',
  furnizor: '',
  stocDisponibil: '0',
};

const normalizeImportKey = (value) => String(value || '').trim().toLowerCase();

const Preforme = ({ currentUser, currentUserRole }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [importing, setImporting] = useState(false);

  const isOperator = currentUserRole === 'Operatori' || currentUserRole === 'Operator';

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/preforme`);
      setRows(res.data);
    } catch (e) {
      console.error('Error loading preforme:', e);
      setError('Eroare la încărcarea preformelor');
    }
  };

  const importRowsLegacy = async (payloadRows) => {
    const existingResponse = await axios.get(`${API_BASE_URL}/preforme`);
    const existingRows = Array.isArray(existingResponse.data) ? existingResponse.data : [];

    let created = 0;
    let updated = 0;
    let skipped = 0;

    for (const row of payloadRows) {
      const cod = String(row.codArt || '').trim();
      const denumire = String(row.denumire || '').trim();
      const stocDisponibil = parseStockValue(row.cant_fin ?? row.stocDisponibil ?? row.valoare);

      if (!cod || !denumire) {
        skipped += 1;
        continue;
      }

      const normalizedCod = normalizeImportKey(cod);
      const normalizedDenumire = normalizeImportKey(denumire);

      const existing = existingRows.find((existingRow) => {
        const rowCod = normalizeImportKey(existingRow.cod);
        const rowDenumire = normalizeImportKey(existingRow.denumire);
        return (rowCod === normalizedCod && rowDenumire === normalizedDenumire)
          || rowCod === normalizedCod
          || rowDenumire === normalizedDenumire;
      });

      if (existing) {
        await axios.put(`${API_BASE_URL}/preforme/${existing.id}`, {
          cod,
          denumire,
          furnizor: existing.furnizor || '',
          stocDisponibil,
          currentUser: currentUser?.username,
        });
        existing.cod = cod;
        existing.denumire = denumire;
        existing.stocDisponibil = stocDisponibil;
        updated += 1;
        continue;
      }

      const createdResponse = await axios.post(`${API_BASE_URL}/preforme`, {
        cod,
        denumire,
        furnizor: '',
        stocDisponibil,
        createdBy: currentUser?.username,
      });
      existingRows.push(createdResponse.data || { cod, denumire, stocDisponibil });
      created += 1;
    }

    return { created, updated, skipped, fallback: true };
  };

  const triggerCSVInput = () => {
    const el = document.getElementById('csv-preforme-input');
    if (el) el.click();
  };

  const handleCSVImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (isOperator) {
      setError('Operatorii nu pot importa preforme');
      e.target.value = '';
      return;
    }

    setImporting(true);
    setError('');

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const text = evt.target.result;
        const lines = String(text).split(/\r?\n/).filter(line => line.trim());
        if (lines.length < 2) {
          throw new Error('Fișierul trebuie să conțină header + cel puțin o linie de date');
        }

        const delimiter = detectDelimiter(lines[0]);
        let headers = parseCSVLine(lines[0], delimiter).map(header => header.trim().toLowerCase());
        if (headers.length) {
          headers[0] = headers[0].replace(/^\uFEFF/, '');
        }

          const requiredHeaders = ['denumire', 'cod_art', 'cant_fin'];
        for (const requiredHeader of requiredHeaders) {
          if (!headers.includes(requiredHeader)) {
            throw new Error(`Lipsește coloana obligatorie "${requiredHeader}"`);
          }
        }

        const payloadRows = [];
        for (let index = 1; index < lines.length; index += 1) {
          const cols = parseCSVLine(lines[index], delimiter);
          if (cols.every(value => !String(value).trim())) {
            continue;
          }

          const getVal = (name) => cols[headers.indexOf(name)]?.trim();
          const denumire = getVal('denumire');
          const codArt = getVal('cod_art');
          const cantFin = getVal('cant_fin');

          if (!denumire || !codArt) {
            continue;
          }

          payloadRows.push({
            denumire,
            codArt,
            cant_fin: parseStockValue(cantFin),
          });
        }

        if (!payloadRows.length) {
          throw new Error('Fișierul nu conține rânduri valide pentru import');
        }

        let response;
        try {
          response = await axios.post(apiUrl('/preforme/import'), {
            rows: payloadRows,
            currentUser: currentUser?.username,
          });
        } catch (err) {
          if (err.response?.status !== 404) {
            throw err;
          }

          const fallbackResult = await importRowsLegacy(payloadRows);
          response = { data: fallbackResult };
        }

        await fetchAll();
        const { created, updated, skipped } = response.data;
        setError(`Import finalizat: ${created} create, ${updated} actualizate, ${skipped} omise.`);
        setTimeout(() => setError(''), 5000);
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Eroare la import');
      } finally {
        setImporting(false);
        e.target.value = '';
      }
    };

    reader.onerror = () => {
      setError('Eroare la citirea fișierului');
      setImporting(false);
      e.target.value = '';
    };

    reader.readAsText(file);
  };

  const handleCSVExport = () => {
    if (!rows.length) {
      setError('Nu există date pentru export');
      return;
    }

    const headers = ['cod', 'denumire', 'furnizor', 'stocDisponibil', 'createdBy'];
    const escapeField = (val) => {
      const s = (val || '').toString().replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };

    const csv = [headers.join(',')]
      .concat(rows.map(row => headers.map(header => escapeField(row[header])).join(',')))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `preforme_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setError('Export realizat cu succes!');
    setTimeout(() => setError(''), 3000);
  };

  const openAdd = () => {
    if (isOperator) {
      setError('Operatorii nu pot adăuga preforme.');
      return;
    }
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (row) => {
    if (isOperator) {
      setError('Operatorii nu pot edita');
      return;
    }
    setEditing(row);
    setForm({
      cod: row.cod,
      denumire: row.denumire,
      furnizor: row.furnizor || '',
      stocDisponibil: String(row.stocDisponibil || 0),
    });
    setShowModal(true);
  };

  const save = async () => {
    if (isOperator) {
      setError('Operatorii nu pot salva modificări.');
      return;
    }
    if (!form.cod.trim() || !form.denumire.trim()) {
      setError('Codul și denumirea sunt obligatorii');
      return;
    }

    let stocDisponibil;
    try {
      stocDisponibil = parseStockValue(form.stocDisponibil);
    } catch (validationError) {
      setError(validationError.message);
      return;
    }

    try {
      setLoading(true);
      if (editing) {
        await axios.put(`${API_BASE_URL}/preforme/${editing.id}`, {
          ...form,
          stocDisponibil,
          currentUser: currentUser?.username,
        });
      } else {
        await axios.post(`${API_BASE_URL}/preforme`, {
          ...form,
          stocDisponibil,
          createdBy: currentUser?.username,
        });
      }
      setShowModal(false);
      setForm(emptyForm);
      setEditing(null);
      await fetchAll();
      setError(editing ? 'Înregistrare actualizată cu succes!' : 'Înregistrare adăugată cu succes!');
      setTimeout(() => setError(''), 3000);
    } catch (e) {
      console.error('Save error:', e);
      setError(e.response?.data?.error || 'Eroare la salvare');
    } finally {
      setLoading(false);
    }
  };

  const remove = async (row) => {
    if (isOperator) {
      setError('Operatorii nu pot șterge');
      return;
    }
    if (!window.confirm(`Sigur doriți să ștergeți preforma "${row.cod} - ${row.denumire}"?`)) {
      return;
    }
    try {
      setLoading(true);
      await axios.delete(`${API_BASE_URL}/preforme/${row.id}`, { data: { currentUser: currentUser?.username } });
      await fetchAll();
      setError('Înregistrare ștearsă cu succes!');
      setTimeout(() => setError(''), 3000);
    } catch (e) {
      console.error('Delete error:', e);
      setError(e.response?.data?.error || 'Eroare la ștergere');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0 }}>🧪 Preforme</h3>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {!isOperator && (
              <>
                <button className="btn btn-primary" onClick={openAdd} disabled={loading}>➕ Adaugă Preformă</button>
                <input id="csv-preforme-input" type="file" accept=".csv" style={{ display: 'none' }} onChange={handleCSVImport} />
                <button className="btn btn-secondary" onClick={triggerCSVInput} disabled={loading || importing} style={{ display: 'flex', gap: '.25rem', alignItems: 'center' }}>
                  {importing ? '📂 Import...' : '📂 Importă CSV'}
                </button>
                <button className="btn btn-success" onClick={handleCSVExport} disabled={loading || importing || rows.length === 0} style={{ display: 'flex', gap: '.25rem', alignItems: 'center' }}>
                  📤 Export CSV
                </button>
              </>
            )}
          </div>
        </div>

        {error && (
          <div
            style={{
              background: error.includes('succes') ? '#d4edda' : '#f8d7da',
              color: error.includes('succes') ? '#155724' : '#721c24',
              padding: '0.75rem',
              borderRadius: '4px',
              marginBottom: '1rem',
              border: `1px solid ${error.includes('succes') ? '#c3e6cb' : '#f5c6cb'}`,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Cod</th>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Denumire</th>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Furnizor</th>
                <th style={{ padding: '0.5rem', textAlign: 'right', borderBottom: '1px solid #dee2e6' }}>Stoc disponibil</th>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Creat de</th>
                <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '0.5rem' }}>{row.cod}</td>
                  <td style={{ padding: '0.5rem' }}>{row.denumire}</td>
                  <td style={{ padding: '0.5rem' }}>{row.furnizor || ''}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>{row.stocDisponibil ?? 0}</td>
                  <td style={{ padding: '0.5rem' }}>{row.createdBy}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                      <button className="btn btn-warning" onClick={() => openEdit(row)} disabled={isOperator} style={{ padding: '0.25rem 0.5rem' }}>✏️ Editează</button>
                      <button className="btn btn-danger" onClick={() => remove(row)} disabled={isOperator} style={{ padding: '0.25rem 0.5rem' }}>🗑️ Șterge</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.45)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: '1rem',
          }}
        >
          <div
            style={{
              background: 'white',
              padding: '1.5rem 1.75rem',
              borderRadius: 12,
              width: '100%',
              maxWidth: 560,
              boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
              display: 'flex',
              flexDirection: 'column',
              animation: 'fadeInScale 0.18s ease-out',
            }}
          >
            <h3 style={{ margin: '0 0 1.25rem', textAlign: 'center', fontSize: '1.25rem' }}>{editing ? '✏️ Editează Preformă' : '➕ Adaugă Preformă'}</h3>
            <div style={{ width: '100%', maxWidth: 420, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Cod</label>
                <input type="text" value={form.cod} onChange={(e) => setForm({ ...form, cod: e.target.value })} style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #ced4da', borderRadius: 6 }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Denumire</label>
                <input type="text" value={form.denumire} onChange={(e) => setForm({ ...form, denumire: e.target.value })} style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #ced4da', borderRadius: 6 }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Furnizor</label>
                <input type="text" value={form.furnizor} onChange={(e) => setForm({ ...form, furnizor: e.target.value })} placeholder="(optional)" style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #ced4da', borderRadius: 6 }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Stoc disponibil</label>
                <input type="text" inputMode="decimal" value={form.stocDisponibil} onChange={(e) => setForm({ ...form, stocDisponibil: e.target.value })} style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #ced4da', borderRadius: 6 }} />
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginTop: '0.5rem' }}>
                <button className="btn btn-secondary" onClick={() => { setShowModal(false); setEditing(null); setForm(emptyForm); }} disabled={loading} style={{ minWidth: 120 }}>Anulează</button>
                <button className="btn btn-primary" onClick={save} disabled={loading} style={{ minWidth: 140 }}>{loading ? 'Se salvează...' : 'Salvează'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Preforme;
